import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/lib/cart";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token-gated cart recovery (P3C). The token is a 128-bit random id emailed
// only to the cart's owner; the backend returns cart items (with current
// prices) and never any customer data.
export default function CartRecoverPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/carts/recover/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (!data.items?.length) {
          setState("empty");
          return;
        }
        cart.clear();
        data.items.forEach((i) =>
          cart.add(
            { sku: i.sku, name: i.name, seo_title: i.name, retail_price: i.price, image_url: i.image },
            i.quantity,
            i.size != null ? { size: i.size, fee: i.sizing_fee || 0 } : null,
            i.engrave ? { text: i.engrave, fee: i.engraving_fee || 0 } : null,
            i.plate || null,
            i.size_label || null
          )
        );
        localStorage.setItem("gt_cart_token", token);
        toast.success("Your cart is back — exactly as you left it");
        navigate("/cart", { replace: true });
      } catch {
        if (!cancelled) setState("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <main className="min-h-screen bg-[#F5F3EC]" data-testid="cart-recover-page">
      <Header />
      <div className="pt-[164px] lg:pt-[196px] pb-32 max-w-xl mx-auto px-6 text-center">
        {state === "loading" && <p className="text-neutral-500 text-sm" data-testid="recover-loading">Restoring your cart…</p>}
        {state === "empty" && (
          <>
            <h1 className="font-display text-3xl font-light text-neutral-900 mb-3">This cart has moved on</h1>
            <p className="text-sm text-neutral-600">Those pieces are no longer saved here — they may have sold or the link expired.</p>
          </>
        )}
        {state === "invalid" && (
          <>
            <h1 className="font-display text-3xl font-light text-neutral-900 mb-3">Link not recognized</h1>
            <p className="text-sm text-neutral-600">This recovery link is invalid or has already been used. Your account's cart is always available after signing in.</p>
          </>
        )}
      </div>
      <Footer />
    </main>
  );
}
